import { Action } from "redux";
import { ThunkAction } from "redux-thunk";
import queryString from "query-string";
import { endpoints } from "../Constants/endpoints";
import { IAppState, ApiRequest } from "../../Common";

import { FeedbackState, FeedbackCategory, FeedbackSortOption } from "../../Feedback";
import ResponseHandler from "../ResponseHandler";
import { ResponseResult } from "../../Common/Models/ResponseResult";

// general interface to describe types that contain an "AppId" which is used as the key in the appSubscriptionsState dictionary in the app state
interface IHasAppId {
  appId: string;
}

interface IAppSubscriptionAction extends Action {
  type: AppSubscriptionActionType;
  payload: any;
}

// ARGUMENT TYPES

// arguments for the getAppSubscriptionStatus action creator (which triggers the APP_SUBSCRIPTION_LOADED action)
export interface IGetAppSubscriptionStatusArguments extends IHasAppId {}

// arguments for the setLoading action creator
export interface ISetAppSubscriptionStatusLoadingArguments extends IHasAppId {}

// arguments for the setErrored action creator
export interface ISetAppSubscriptionStatusErroredArguments extends IHasAppId {
  errorCode: number;
  errorMessage: string;
}

// arguments for the unload action creator
export interface IUnloadAppSubscriptionStatusArguments extends IHasAppId {}

// PAYLOAD TYPES

// Payload for the APP_SUBSCRIPTION_LOADED action, contains the app subscription status
export interface IAppSubscriptionStatusLoadedActionPayload extends IHasAppId {
  appName: string;
  isSubscribed: boolean;
  category?: FeedbackCategory;
}

// Payload for the APP_SUBSCRIPTION_SET_LOADING action
export interface ISetAppSubscriptionStatusLoadingActionPayload extends IHasAppId {}

// Payload for the APP_SUBSCRIPTION_SET_ERRORED action
export interface ISetAppSubscriptionStatusErroredActionPayload extends IHasAppId {
  errorCode: number;
  errorMessage: string;
}

// Payload for the APP_SUBSCRIPTION_UNLOAD action
export interface IUnloadAppSubscriptionActionPayload extends IHasAppId {}

// ACTION TYPES

// action for the APP_SUBSCRIPTION_LOADED action
export interface IAppSubscriptionLoadedAction extends IAppSubscriptionAction {
  payload: IAppSubscriptionStatusLoadedActionPayload;
}

// action for the APP_SUBSCRIPTION_SET_LOADING action
export interface IAppSubscriptionStatusLoadingAction extends IAppSubscriptionAction {
  payload: ISetAppSubscriptionStatusLoadingActionPayload;
}

// action for the APP_SUBSCRIPTION_SET_ERRORED action
export interface IAppSubscriptionStatusErroredAction extends IAppSubscriptionAction {
  payload: ISetAppSubscriptionStatusErroredActionPayload;
}

// action for the APP_SUBSCRIPTION_UNLOAD action
export interface IUnloadAppSubscriptionAction extends IAppSubscriptionAction {
  payload: IUnloadAppSubscriptionActionPayload;
}

// Union of all action types. If you define a new action type, add it here please
export type AppSubscriptionAction =
  | IAppSubscriptionLoadedAction
  | IAppSubscriptionStatusLoadingAction
  | IAppSubscriptionStatusErroredAction
  | IUnloadAppSubscriptionAction;

export enum AppSubscriptionActionType {
  APP_SUBSCRIPTION_LOADED = "APP_SUBSCRIPTION_LOADED",
  APP_SUBSCRIPTION_SET_LOADING = "APP_SUBSCRIPTION_SET_LOADING",
  APP_SUBSCRIPTION_SET_ERRORED = "APP_SUBSCRIPTION_SET_ERRORED",
  APP_SUBSCRIPTION_UNLOAD = "APP_SUBSCRIPTION_UNLOAD"
}

// Action Creator for IAppSubscriptionStatusLoadingAction
export const setAppSubscriptionStatusLoading = (
  args: ISetAppSubscriptionStatusLoadingArguments
): IAppSubscriptionStatusLoadingAction => ({
  type: AppSubscriptionActionType.APP_SUBSCRIPTION_SET_LOADING,
  payload: { ...args }
});

// Action Creator for IAppSubscriptionStatusErroredAction
export const setAppSubscriptionStatusErrored = (
  args: ISetAppSubscriptionStatusErroredArguments
): IAppSubscriptionStatusErroredAction => ({
  type: AppSubscriptionActionType.APP_SUBSCRIPTION_SET_ERRORED,
  payload: { ...args }
});

// Action Creator for IUnloadAppSubscriptionAction
export const unloadAppSubscriptionStatus = (
  args: IUnloadAppSubscriptionStatusArguments
): IUnloadAppSubscriptionAction => ({
  type: AppSubscriptionActionType.APP_SUBSCRIPTION_UNLOAD,
  payload: { ...args }
});

function assembleGetAppSubscriptionStatusUrl(args: IGetAppSubscriptionStatusArguments): string {
  return endpoints.subscriptions.status(args.appId);
}

// gets app subscription status from the server asynchronously and dispatches an APP_SUBSCRIPTION_LOADED action
export const getAppSubscriptionStatus = (
  args: IGetAppSubscriptionStatusArguments
): ThunkAction<
  void,
  IAppState,
  {},
  IAppSubscriptionLoadedAction | IAppSubscriptionStatusErroredAction
> => dispatch => {
  ApiRequest.fromPath(assembleGetAppSubscriptionStatusUrl(args))
    .withAuthHeader()
    .get()
    .then((response: Response) => {
      ResponseHandler.handle(
        response,
        (result: ResponseResult) => {
          // success
          dispatch({
            type: AppSubscriptionActionType.APP_SUBSCRIPTION_LOADED,
            payload: { ...result.payload, appId: args.appId }
          });
        },
        (result: ResponseResult) => {
          // error in correct format
          dispatch(
            setAppSubscriptionStatusErrored({
              ...args,
              errorCode: response.status,
              errorMessage: result.error.message
            })
          );
        },
        () => {
          // error in incorrect format
          dispatch(
            setAppSubscriptionStatusErrored({
              ...args,
              errorCode: response.status,
              errorMessage: "An unknown error has occured"
            })
          );
        }
      );
    });
};
